import { Fragment, type ReactElement, useCallback, useEffect, useId, useState } from 'react';
import { type Nullable } from '@direct-frontend/stdlib';
import { Form } from '@adv-frontend/form-kit';
import { type ServiceType } from '@/shared/api/order';
import { Drawer } from '@/shared/components/Drawer';
import { type IntervalFieldValue } from '@/shared/components/IntervalInput';
import { DeliveryIntervalForm } from './components/DeliveryIntervalForm';
import { deliveryIntervalForm } from './model/deliveryIntervalForm';
import type { OzonDeliveryInterval } from '../../../../lib';

interface DeliveryIntervalPickerPayload {
    onOpenModal: VoidFunction;
}

type Interval = {
    from: number;
    to: number;
    serviceType: ServiceType;
    price: Nullable<string>;
    currency: Nullable<string>;
};

interface DeliveryIntervalPickerProps {
    value: IntervalFieldValue;
    onChange?: (value: IntervalFieldValue) => void;
    children: (payload: DeliveryIntervalPickerPayload) => ReactElement;
    intervals: Interval[];
    deliveryMethod: Nullable<string>;
    ozonDeliveryInterval: OzonDeliveryInterval;
}

export const DeliveryIntervalPicker = (props: DeliveryIntervalPickerProps) => {
    const { value, children, onChange: propOnChange, intervals, deliveryMethod, ozonDeliveryInterval } = props;
    const formId = useId();
    const [isOpen, setIsOpen] = useState(false);
    const onOpenModal = useCallback(() => setIsOpen(true), [setIsOpen]);

    const onCloseModal = useCallback(() => {
        setIsOpen(false);
        deliveryIntervalForm.getInstance(formId).reset();
    }, [formId, setIsOpen]);

    const onChange = useCallback(
        (values: IntervalFieldValue) => {
            propOnChange?.(values);
            setIsOpen(false);
        },
        [setIsOpen, propOnChange]
    );

    useEffect(() => {
        deliveryIntervalForm.getInstance(formId).reinitialize({ deliveryInterval: value });
    }, [formId, value]);

    return (
        <Fragment>
            <Form id={formId} form={deliveryIntervalForm} initialValue={{ deliveryInterval: value }}>
                <Drawer isOpen={isOpen} onClose={onCloseModal}>
                    <DeliveryIntervalForm
                        deliveryMethod={deliveryMethod}
                        formId={formId}
                        onSubmit={onChange}
                        intervals={intervals}
                        ozonDeliveryInterval={ozonDeliveryInterval}
                    />
                </Drawer>
            </Form>
            {children({ onOpenModal })}
        </Fragment>
    );
};
